import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { Button, Input, Label, Switch } from '@shared/ui';
import { DecimalUsdtInput } from './decimal-usdt-input';
import { AutoWithdrawAddressHint } from './auto-withdraw-address-hint';
import { ThresholdHint } from './threshold-hint';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';
import type { AutoWithdrawalRuleDraft } from '@entities/withdrawal';
import { sanitizeWithdrawalAddressInput } from '../../lib/manual-form';
import type { AutoWithdrawalRuleFieldErrors } from '../../lib/validation';
import { AmountStrategyFields } from './amount-strategy-fields';
import type { WithdrawalAddressItem } from '@shared/api/contracts/security';
import { AddressChangeNotice, RuleAddressState, VerificationBadge } from './verification-badge';

interface DirectRuleCardProps {
  draft: AutoWithdrawalRuleDraft;
  pairBalance: string;
  disabled?: boolean;
  priorityRank?: number;
  showPriority?: boolean;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  errors?: AutoWithdrawalRuleFieldErrors;
  addresses?: WithdrawalAddressItem[];
  language?: string;
  cardRef?: RefObject<HTMLDivElement | null>;
  onEnabledChange: (enabled: boolean) => void;
  onAddressChange: (address: string) => void;
  onAmountModeChange: (mode: AutoWithdrawalRuleDraft['amountMode']) => void;
  onThresholdChange: (value: string) => void;
  onReserveChange: (value: string) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

export const DirectRuleCard = ({
  draft,
  pairBalance,
  disabled = false,
  priorityRank,
  showPriority = false,
  canMoveUp = false,
  canMoveDown = false,
  errors,
  addresses = [],
  language = 'en',
  cardRef,
  onEnabledChange,
  onAddressChange,
  onAmountModeChange,
  onThresholdChange,
  onReserveChange,
  onMoveUp,
  onMoveDown,
}: DirectRuleCardProps) => {
  const { t } = useTranslation('cabinet');
  const titleId = `auto-direct-title-${draft.key}`;

  return (
    <div ref={cardRef} className="overflow-visible rounded-xl border border-border p-4">
      <div className="flex items-start gap-3">
        {priorityRank != null && (
          <span
            aria-hidden
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-foreground"
          >
            {priorityRank}
          </span>
        )}

        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 id={titleId} className="text-sm font-semibold">
              {draft.assetCode} · {draft.networkCode}
            </h4>
            <VerificationBadge draft={draft} />
          </div>
          <p className="text-xs text-muted-foreground">
            {t('withdrawal.auto.direct.currentBalance', {
              amount: formatLedgerAmount(pairBalance, { assetLabel: draft.assetCode }),
            })}
          </p>
          <RuleAddressState draft={draft} items={addresses} language={language} />
        </div>

        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          {showPriority && draft.enabled && (
            <div className="flex gap-1">
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                disabled={disabled || !canMoveUp}
                aria-label={t('withdrawal.auto.priority.moveUp', { asset: draft.assetCode })}
                onClick={onMoveUp}
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                disabled={disabled || !canMoveDown}
                aria-label={t('withdrawal.auto.priority.moveDown', { asset: draft.assetCode })}
                onClick={onMoveDown}
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
            </div>
          )}
          <Switch
            checked={draft.enabled}
            disabled={disabled}
            onCheckedChange={onEnabledChange}
            aria-labelledby={titleId}
          />
        </div>
      </div>

      {draft.enabled && (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div className="min-w-0 space-y-2">
              <Label htmlFor={`auto-direct-address-${draft.key}`}>{t('withdrawal.auto.fields.address')}</Label>
              <Input
                id={`auto-direct-address-${draft.key}`}
                value={draft.destinationAddress}
                disabled={disabled}
                onChange={(event) => onAddressChange(sanitizeWithdrawalAddressInput(event.target.value))}
                invalid={Boolean(errors?.destinationAddress)}
                className="font-mono text-[13px]"
                spellCheck={false}
                autoComplete="off"
              />
              <AutoWithdrawAddressHint
                address={draft.destinationAddress}
                networkCode={draft.networkCode}
                assetCode={draft.assetCode}
                fieldError={errors?.destinationAddress}
              />
              <AddressChangeNotice draft={draft} />
            </div>
            <div className="min-w-0 space-y-2">
              <Label htmlFor={`auto-direct-threshold-${draft.key}`}>{t('withdrawal.auto.fields.threshold')}</Label>
              <DecimalUsdtInput
                id={`auto-direct-threshold-${draft.key}`}
                value={draft.thresholdAmount}
                assetCode={draft.assetCode}
                disabled={disabled}
                error={errors?.thresholdAmount}
                onChange={onThresholdChange}
              />
              <ThresholdHint
                thresholdAmount={draft.thresholdAmount}
                assetCode={draft.assetCode}
                balanceAmount={pairBalance}
              />
            </div>
          </div>

          <AmountStrategyFields
            draft={draft}
            disabled={disabled}
            reserveError={errors?.reserveAmount}
            amountModeError={errors?.amountMode}
            onAmountModeChange={onAmountModeChange}
            onThresholdChange={onThresholdChange}
            onReserveChange={onReserveChange}
            reserveId={`auto-direct-reserve-${draft.key}`}
            amountModeId={`auto-direct-amount-mode-${draft.key}`}
            showThreshold={false}
          />
        </div>
      )}
    </div>
  );
};
